// AI-ASSISTED: generated with claude-code on 2026-08-26, reviewed by <dev>
//
// Thin client over the backend plugin's /api/maintenance/* proxy
// (contracts/maintenance-backend-api.md). Uses discoveryApi + fetchApi so
// every request carries the user's Backstage session — the browser never
// talks to maintenance-mode-service directly. Write methods are added
// incrementally alongside the user stories that need them (tasks
// T027/T034/T049); createSchedule (T027) is the first.

import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  Application,
  ApplicationStatusMap,
  CreateSchedulePayload,
  MaintenanceSchedule,
  SchedulesQuery,
  UpdateApplicationPayload,
} from './maintenance.types';

export class MaintenanceApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'MaintenanceApiError';
  }
}

export class MaintenanceApiClient {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  private async baseUrl(): Promise<string> {
    return this.deps.discoveryApi.getBaseUrl('maintenance');
  }

  private async request<T>(
    path: string,
    init: { method?: string; body?: unknown } = {},
  ): Promise<T> {
    const baseUrl = await this.baseUrl();
    const method = init.method ?? 'GET';
    const url = `${baseUrl}${path}`;
    // Plain reads keep the single-argument fetch(url) call (no method/body
    // needed); any explicit method (writes) passes the init object, with a
    // JSON body only when one was actually given.
    const res =
      init.method === undefined
        ? await this.deps.fetchApi.fetch(url)
        : await this.deps.fetchApi.fetch(url, {
            method,
            headers: init.body === undefined ? undefined : { 'Content-Type': 'application/json' },
            body: init.body === undefined ? undefined : JSON.stringify(init.body),
          });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      const bodySuffix = body ? `: ${body}` : '';
      throw new MaintenanceApiError(
        res.status,
        `${method} ${path} → ${res.status}${bodySuffix}`,
      );
    }
    return (await res.json()) as T;
  }

  /**
   * Never throws on a disabled feature — used by MaintenanceSidebarItem and
   * the page shell to decide whether to render at all (spec FR-001).
   */
  async enabled(): Promise<boolean> {
    try {
      const baseUrl = await this.baseUrl();
      const res = await this.deps.fetchApi.fetch(`${baseUrl}/enabled`);
      if (!res.ok) return false;
      const data = (await res.json()) as { enabled?: boolean };
      return Boolean(data.enabled);
    } catch {
      return false;
    }
  }

  getSchedules(query: SchedulesQuery = {}): Promise<MaintenanceSchedule[]> {
    const params = new URLSearchParams();
    for (const name of query.applicationNames ?? []) {
      params.append('applicationNames', name);
    }
    for (const status of query.statuses ?? []) {
      params.append('statuses', status);
    }
    if (query.from) params.set('from', query.from);
    if (query.until) params.set('until', query.until);
    const qs = params.toString();
    const queryString = qs ? `?${qs}` : '';
    return this.request<MaintenanceSchedule[]>(`/schedules${queryString}`);
  }

  getApplicationSchedules(name: string): Promise<MaintenanceSchedule[]> {
    return this.request<MaintenanceSchedule[]>(
      `/schedules/application/${encodeURIComponent(name)}`,
    );
  }

  getApplications(): Promise<Application[]> {
    return this.request<Application[]>('/applications');
  }

  getApplicationsStatus(): Promise<ApplicationStatusMap> {
    return this.request<ApplicationStatusMap>('/applications/status');
  }

  /** Plans a new maintenance window (spec US2). Date validation is not
   * duplicated here — the service's 4xx response is surfaced as-is via
   * MaintenanceApiError. */
  createSchedule(payload: CreateSchedulePayload): Promise<MaintenanceSchedule> {
    return this.request<MaintenanceSchedule>('/schedules', {
      method: 'POST',
      body: payload,
    });
  }

  /** Cancels a Planned or Active window (spec US3, FR-015). */
  cancelSchedule(id: string): Promise<MaintenanceSchedule> {
    return this.request<MaintenanceSchedule>(
      `/schedules/${encodeURIComponent(id)}/cancel`,
      { method: 'PATCH' },
    );
  }

  /** Updates an application's explicit status and/or health-check endpoint (spec US6, FR-020). */
  updateApplication(name: string, payload: UpdateApplicationPayload): Promise<Application> {
    return this.request<Application>(`/applications/${encodeURIComponent(name)}`, {
      method: 'PATCH',
      body: payload,
    });
  }

  /** Adds a dependency to an application (spec US6, FR-021). */
  addDependency(name: string, dependencyName: string): Promise<Application> {
    return this.request<Application>(
      `/applications/${encodeURIComponent(name)}/dependencies`,
      { method: 'POST', body: { dependencyName } },
    );
  }

  /** Removes a dependency from an application (spec US6, FR-021). */
  removeDependency(name: string, dependencyName: string): Promise<Application> {
    return this.request<Application>(
      `/applications/${encodeURIComponent(name)}/dependencies/${encodeURIComponent(dependencyName)}`,
      { method: 'DELETE' },
    );
  }
}
